import React, { useEffect, useState } from 'react';
import quantService from '../../../services/quantService';

const UniverseBrowser = () => {
  const [universes, setUniverses] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [asOf, setAsOf] = useState('');
  const [members, setMembers] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    quantService.listUniverses()
      .then(({ data }) => {
        setUniverses(data || []);
        if (data?.length) setSelectedId(data[0].id);
      })
      .catch(e => setError(e?.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    quantService.getMembership(selectedId, asOf)
      .then(({ data }) => {
        setMembers(data.members || []);
        setMemberCount(data.count || 0);
      })
      .catch(e => setError(e?.response?.data?.detail || e.message));
  }, [selectedId, asOf]);

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    setInfo(null);
    try {
      const { data } = await quantService.refreshUniverse(selectedId);
      setInfo(`Queued task ${data.task_id}. Reload in ~30s to see updates.`);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = members.filter(m =>
    !search || m.symbol.toLowerCase().includes(search.toLowerCase())
              || (m.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Universe</label>
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded">
            {universes.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">As of date</label>
          <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
                 placeholder="present"
                 className="px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
        </div>
        <div className="flex-1">
          <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Search</label>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="symbol or name"
                 className="w-full px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
        </div>
        <button onClick={refresh} disabled={refreshing || !selectedId}
                className="px-3 py-1.5 text-xs uppercase tracking-wider bg-brand-gold/10 text-brand-gold border border-brand-gold/30 rounded hover:bg-brand-gold/20 disabled:opacity-40">
          {refreshing ? 'Queueing…' : 'Refresh from source'}
        </button>
      </div>

      {info && <div className="text-xs text-emerald-500">{info}</div>}
      {error && <div className="text-xs text-red-500">{error}</div>}

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex justify-between">
          <span className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Members {asOf ? `as of ${asOf}` : 'currently'}
          </span>
          <span className="text-sm font-mono text-gray-500">
            {filtered.length}/{memberCount}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Symbol</th>
                <th className="text-left px-4 py-2 hidden sm:table-cell">Name</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Sector</th>
                <th className="text-left px-4 py-2">From</th>
                <th className="text-left px-4 py-2">Through</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 1000).map(m => (
                <tr key={m.id} className="border-t border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                  <td className="px-4 py-1.5 font-mono text-xs">{m.symbol}</td>
                  <td className="px-4 py-1.5 text-xs hidden sm:table-cell truncate max-w-[200px] md:max-w-none">{m.name}</td>
                  <td className="px-4 py-1.5 text-xs hidden md:table-cell">{m.sector}</td>
                  <td className="px-4 py-1.5 text-xs font-mono text-gray-500">{m.from_date}</td>
                  <td className="px-4 py-1.5 text-xs font-mono">
                    {m.through_date
                      ? <span className="text-gray-500">{m.through_date}</span>
                      : <span className="text-emerald-500">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 1000 && (
            <div className="text-center text-[10px] text-gray-400 py-2 border-t border-gray-100 dark:border-gray-800/50">
              Showing first 1000 of {filtered.length} matches.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UniverseBrowser;
